/**
 * Guided Empty State Component
 *
 * Displays a contextual empty state with illustration, description,
 * and action button that links to the next workflow step. Used on
 * pages/tabs where no data exists yet, providing workflow-aware
 * guidance instead of a generic "No Data" message.
 *
 * @module components/EmptyState
 * @spec 135-ui-data-alignment
 * @story US2 - Workflow Guidance
 * @constitution v1.12.0 - Principle XXXVII (Code Header Standards)
 */

import React from 'react';
import { Link } from 'react-router-dom';
import {
  Database,
  Server,
  Shield,
  Calendar,
  HardDrive,
  ArrowRight,
  type LucideIcon,
} from 'lucide-react';

// =============================================================================
// ICON MAPPING
// =============================================================================

const ICON_MAP: Record<string, LucideIcon> = {
  database: Database,
  server: Server,
  shield: Shield,
  calendar: Calendar,
  storage: HardDrive,
};

// =============================================================================
// PROPS
// =============================================================================

export interface GuidedEmptyStateProps {
  /** Icon name to display (maps to Lucide icons) */
  icon?: keyof typeof ICON_MAP;
  /** Main title (e.g., "No Storage Accounts Mapped") */
  title: string;
  /** Descriptive text explaining what the user should do */
  description: string;
  /** Button label for the primary action */
  actionLabel?: string;
  /** Route to navigate to when action button is clicked */
  actionPath?: string;
  /** Workflow step context (e.g., "Step 3 of 6: Assign Storage Account") */
  workflowStep?: string;
}

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * GuidedEmptyState — Replaces generic "No Data" states with workflow-aware guidance.
 *
 * When a page/tab has no data, this component explains why and guides the user
 * to the appropriate next step in the workflow.
 */
export const GuidedEmptyState: React.FC<GuidedEmptyStateProps> = ({
  icon = 'database',
  title,
  description,
  actionLabel,
  actionPath,
  workflowStep,
}) => {
  const IconComponent = ICON_MAP[icon] || Database;

  return (
    <div
      className="flex flex-col items-center justify-center py-12 px-6 text-center"
      role="status"
      aria-label={title}
    >
      {/* Icon */}
      <div className="p-4 bg-gray-100 dark:bg-gray-800 rounded-full mb-4">
        <IconComponent
          className="w-10 h-10 text-gray-400 dark:text-gray-500"
          aria-hidden="true"
        />
      </div>

      {/* Title */}
      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
        {title}
      </h3>

      {/* Description */}
      <p className="text-sm text-gray-600 dark:text-gray-400 max-w-md mb-4">
        {description}
      </p>

      {/* Workflow Step Context */}
      {workflowStep && (
        <p className="text-xs text-blue-600 dark:text-blue-400 font-medium mb-4">
          {workflowStep}
        </p>
      )}

      {/* Action Button */}
      {actionLabel && actionPath && (
        <Link
          to={actionPath}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 rounded-md transition-colors"
        >
          {actionLabel}
          <ArrowRight className="w-4 h-4" aria-hidden="true" />
        </Link>
      )}
    </div>
  );
};

export default GuidedEmptyState;
